/**
 * Vehicle Data Extractor - Professional utility for extracting vehicle information from URLs
 * Uses intelligent pattern recognition without hardcoded values
 */

/**
 * Extract vehicle data from URL using intelligent pattern recognition
 * @param {string} pathname - Current URL path
 * @returns {Object} Vehicle data { make, model }
 */
export function extractVehicleDataFromPath(pathname) {
  const pathSegments = pathname.split('/').filter(Boolean)
  let make = ''
  let model = ''

  // Skip non-vehicle pages using intelligent detection
  if (isNonVehiclePage(pathSegments)) {
    return { make: '', model: '' }
  }

  // Pattern-based extraction following application structure
  const vehicleInfo = extractFromPatterns(pathSegments)
  make = vehicleInfo.make
  model = vehicleInfo.model

  return { make, model }
}

/**
 * Intelligent detection of non-vehicle pages using structural analysis
 * @param {Array} pathSegments - URL path segments
 * @returns {boolean} True if non-vehicle page
 */
function isNonVehiclePage(pathSegments) {
  if (pathSegments.length === 0) return true

  const firstSegment = pathSegments[0].toLowerCase()

  // Structural detection - identify non-vehicle patterns
  return (
    // Common site sections
    isSiteSection(firstSegment) ||
    // Legal/utility pages
    isLegalPage(firstSegment) ||
    // Service/support pages
    isServicePage(firstSegment)
  )
}

/**
 * Check if path segment represents a main site section
 * @param {string} segment - URL path segment
 * @returns {boolean} True if site section
 */
function isSiteSection(segment) {
  const siteSections = [
    'locations', 'about', 'about-us', 'contact', 'news', 'careers', 'help'
  ]
  return siteSections.includes(segment)
}

/**
 * Check if path segment represents legal/utility page
 * @param {string} segment - URL path segment
 * @returns {boolean} True if legal page
 */
function isLegalPage(segment) {
  const legalPages = [
    'legal', 'privacy', 'terms', 'cookies', 'accessibility', 'sitemap'
  ]
  return legalPages.includes(segment)
}

/**
 * Check if path segment represents service/support page
 * @param {string} segment - URL path segment
 * @returns {boolean} True if service page
 */
function isServicePage(segment) {
  const servicePages = [
    'search', 'finance', 'insurance', 'parts', 'service', 'motability',
    'business', 'fleet', 'reviews'
  ]
  return servicePages.includes(segment)
}

/**
 * Extract vehicle data based on URL patterns
 * @param {Array} pathSegments - URL path segments
 * @returns {Object} { make, model }
 */
function extractFromPatterns(pathSegments) {
  let make = ''
  let model = ''

  // Pattern 1: /cars/used/make/model/stockId or /vans/used/make/model/stockId
  if (pathSegments.length >= 5) {
    const category = pathSegments[0]
    const condition = pathSegments[1]
    if (isVehicleCategory(category) && condition === 'used') {
      make = pathSegments[2] || ''
      model = pathSegments[3] || ''
    }
  }
  // Pattern 2: /cars/make/model or /vans/make/model
  else if (pathSegments.length >= 3) {
    const category = pathSegments[0]
    if (isVehicleCategory(category)) {
      make = pathSegments[1] || ''
      model = pathSegments[2] || ''
    }
  }
  // Pattern 3: /make/model (direct vehicle pages)
  else if (pathSegments.length === 2) {
    make = pathSegments[0] || ''
    model = pathSegments[1] || ''
  }
  // Pattern 4: /make (brand pages)
  else if (pathSegments.length === 1) {
    make = pathSegments[0] || ''
  }

  return { make, model }
}

/**
 * Check if category represents vehicle type
 * @param {string} category - URL category segment
 * @returns {boolean} True if vehicle category
 */
function isVehicleCategory(category) {
  const vehicleCategories = ['cars', 'vans']
  return vehicleCategories.includes(category.toLowerCase())
}
